import type { UniqueIdentifier } from '@dnd-kit/core';
import useOptimisticSnapshot, {
  idKey,
  type SnapshotEntry,
} from '../optimistic/useOptimisticSnapshot';

interface BaseItem {
  id: UniqueIdentifier;
}

const snapshot = (list: BaseItem[]): SnapshotEntry[] =>
  list.map((item) => ({ id: item.id, key: idKey(item.id) }));

const reapply = <T extends BaseItem>(
  target: SnapshotEntry[],
  list: T[]
): T[] => {
  const byId = new Map(list.map((item) => [item.id, item]));
  return target.map((entry) => byId.get(entry.id)!);
};

/**
 * Bridges the one-frame "snap-back" flash that appears when a sortable list
 * is backed by server state and updated optimistically.
 *
 * The flash happens because dnd-kit's `onDragEnd` and TanStack Query's
 * optimistic cache write both run synchronously, but TanStack's
 * `notifyManager` defers the cache-change notification to a macrotask. For
 * one render the source `items` prop still holds the pre-drop order, so
 * the dragged item visibly snaps back before landing.
 *
 * Usage: call `commit(newItems)` from `onDragEnd` to record the new id
 * sequence, then render from `displayItems` instead of the raw `items`
 * prop. `displayItems` reorders the live `items` by the pending sequence,
 * so unrelated edits to individual items (e.g. an in-flight title save)
 * still flow through.
 *
 * The pending order auto-clears when the source `items` catch up to it, when
 * the source produces a different order (which then wins), or when the id set
 * diverges. Because the clear is driven by the `items` prop rather than mutation
 * callbacks or React render timing, the source `useQuery` can live anywhere in
 * the tree — no colocation or `onMutate` wiring required. See
 * {@link useOptimisticSnapshot} for the shared mechanism.
 *
 * Reference: https://github.com/clauderic/dnd-kit/discussions/1522.
 */
const useOptimisticReorder = <T extends BaseItem>(items: T[]) =>
  useOptimisticSnapshot<T[], SnapshotEntry>(items, { snapshot, reapply });

export default useOptimisticReorder;
